'use client'

import { useCallback, useEffect, useState } from 'react'
import type { TenantPermissionId } from '@/lib/permissions/tenantPermissionIds'

type PermissionState = {
  allowed: boolean
  loading: boolean
  refresh: () => void
}

/**
 * Tenant yetki kontrolü (eczacı bypass). Alt kullanıcı için /permissions/check.
 * Varsayılan: loading bitene kadar false — menü/flash için loading'i dikkate alın.
 */
export function useTenantPermission(permissionId: TenantPermissionId | string | null): PermissionState {
  const [allowed, setAllowed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((n) => n + 1), [])

  useEffect(() => {
    let cancelled = false

    async function run() {
      if (!permissionId) {
        if (!cancelled) {
          setAllowed(false)
          setLoading(false)
        }
        return
      }

      setLoading(true)
      try {
        const userType = typeof window !== 'undefined' ? localStorage.getItem('userType') : null
        if (userType === 'tenant') {
          if (!cancelled) {
            setAllowed(true)
            setLoading(false)
          }
          return
        }

        const tenantId = localStorage.getItem('tenantId')
        const username = localStorage.getItem('username')
        if (!tenantId || !username) {
          if (!cancelled) {
            setAllowed(false)
            setLoading(false)
          }
          return
        }

        const res = await fetch(
          `/api/opas/permissions/check?permission=${encodeURIComponent(permissionId)}`,
          {
            headers: {
              'X-TenantId': tenantId,
              'X-Username': username,
              'X-UserType': userType ?? 'subuser',
            },
            credentials: 'include',
          }
        )
        if (!res.ok) {
          if (!cancelled) setAllowed(false)
          return
        }
        const data = (await res.json()) as { hasPermission?: boolean }
        if (!cancelled) setAllowed(Boolean(data.hasPermission))
      } catch {
        if (!cancelled) setAllowed(false)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [permissionId, tick])

  return { allowed, loading, refresh }
}
